/**
 * Balken „Neue Version verfügbar" unter der Kopfzeile — gleiche Optik wie der
 * Hinweis „Benachrichtigungen erlauben" (notifications/NotificationSetupHinweis).
 * „Jetzt neu laden" lädt die Seite; „Später" blendet ihn 30 Minuten aus, danach
 * kommt er beim nächsten Prüfen wieder. „Was ist neu?" — als Knopf neben „Jetzt
 * neu laden" und als (i) an der Überschrift — zeigt in Stichpunkten, was die neue
 * Version bringt (`aenderungen.ts`). Das Popup ist bewusst kein Fluent-Dialog,
 * sondern im Stil des Balkens gebaut (User 28.09.2026).
 *
 * Seit 01.10.2026 zeigt es zusätzlich, was frühere Releases gebracht haben und
 * die Person noch nicht gelesen hat (nach Tagen gruppiert, `wasIstNeuStand.ts`
 * merkt das Gelesene), und ist über „Was ist neu?" im Benutzermenü auch ohne
 * neue Version erreichbar.
 */
import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { ArrowSync20Regular, Dismiss16Regular, Info16Regular } from "@fluentui/react-icons";
import {
  ERSTES_MAL_TAGE,
  ladeAenderungen,
  MAX_EINTRAEGE,
  tageZurueck,
  wasIstNeuAnsicht,
  type AenderungKurz,
  type AenderungsGruppe,
  type GesehenStand,
  type WasIstNeuModus,
} from "./aenderungen";
import { ladeGesehen, merkeGesehen, WAS_IST_NEU_EREIGNIS } from "./wasIstNeuStand";
import { useUpdateVerfuegbar } from "./updatePruefung";
import { setReloadWarningSuppressed } from "../../utils/reloadWarningSuppress";
import { alleDokumente, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

const AUBERGINE = "#7d2169";
const SCHRIFT = "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif";

const knopfStil = (haupt: boolean): React.CSSProperties => ({
  appearance: "none",
  cursor: "pointer",
  fontFamily: SCHRIFT,
  fontSize: "0.85rem",
  fontWeight: 600,
  padding: "8px 14px",
  minHeight: 36,
  borderRadius: 9,
  whiteSpace: "nowrap",
  border: haupt ? "none" : `1px solid rgba(125,33,105,0.28)`,
  background: haupt ? AUBERGINE : "transparent",
  color: haupt ? "#ffffff" : AUBERGINE,
});

type Stand =
  | { art: "laedt" }
  | { art: "fertig"; liste: unknown; gesehen: GesehenStand | null }
  | { art: "fehler" };

const ABSCHNITTE: Array<{ art: AenderungKurz["art"]; titel: string }> = [
  { art: "neu", titel: "Neu" },
  { art: "behoben", titel: "Korrigiert" },
];

const TEXT = "#241522";
const linkStil: React.CSSProperties = {
  appearance: "none",
  border: "none",
  background: "transparent",
  padding: 0,
  alignSelf: "flex-start",
  cursor: "pointer",
  fontFamily: SCHRIFT,
  fontSize: "0.82rem",
  fontWeight: 600,
  color: AUBERGINE,
  textDecoration: "underline",
};
const LEISE = "#5b4a58";

/** Ein Stichpunkt: Bereich, Kernaussage fett, darunter die Beschreibung, falls der Titel mehr hergibt. */
const Stichpunkt: React.FC<{ e: AenderungKurz }> = ({ e }) => (
  <li style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
    <span
      aria-hidden
      style={{ width: 6, height: 6, borderRadius: 999, background: AUBERGINE, flexShrink: 0, marginTop: 8 }}
    />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "0.9rem", color: TEXT, lineHeight: 1.4 }}>
        {e.bereich && (
          <span
            style={{
              display: "inline-block",
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              color: AUBERGINE,
              background: "rgba(125,33,105,0.08)",
              borderRadius: 6,
              padding: "1px 6px",
              marginRight: 6,
              verticalAlign: "1px",
            }}
          >
            {e.bereich}
          </span>
        )}
        <span style={{ fontWeight: 600 }}>{e.punkt}</span>
      </div>
      {e.details.length > 0 && (
        <ul style={{ margin: "3px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
          {e.details.map((d, i) => (
            <li key={i} style={{ fontSize: "0.83rem", color: LEISE, lineHeight: 1.4 }}>
              – {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  </li>
);

/** Popup „Was ist neu?" — eigenes Overlay statt Fluent-Dialog; lädt die Liste erst beim Öffnen. */
export const WasIstNeuDialog: React.FC<{
  offen: boolean;
  onClose: () => void;
  laufendeCommits?: string;
  /** Nur am Hinweis „Neue Version verfügbar": „Jetzt neu laden" auch im Popup. */
  onNeuLaden?: () => void;
  /** Für Tests; sonst der heutige Tag. */
  heute?: Date;
}> = ({ offen, onClose, laufendeCommits = import.meta.env.VITE_APP_COMMITS, onNeuLaden, heute }) => {
  const [stand, setStand] = React.useState<Stand>({ art: "laedt" });
  // „… und N weitere“ klappt die übrigen Einträge auf (User 01.10.2026).
  const [alleZeigen, setAlleZeigen] = React.useState(false);
  // „Frühere Änderungen anzeigen": auch schon Gelesenes der letzten 14 Tage.
  const [modus, setModus] = React.useState<WasIstNeuModus>("ungelesen");
  const schliessenRef = React.useRef<HTMLButtonElement | null>(null);
  // Was in diesem Öffnen tatsächlich zu sehen war — gilt beim Schließen als gelesen.
  const angezeigtRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!offen) return;
    let lebt = true;
    setStand({ art: "laedt" });
    setAlleZeigen(false);
    setModus("ungelesen");
    angezeigtRef.current = new Set();
    Promise.all([ladeAenderungen(), ladeGesehen().catch(() => null)])
      .then(([liste, gesehen]) => lebt && setStand({ art: "fertig", liste, gesehen }))
      .catch(() => lebt && setStand({ art: "fehler" }));
    return () => {
      lebt = false;
    };
  }, [offen, laufendeCommits]);

  const heuteTag = heute ?? new Date();
  const ansicht = React.useMemo(
    () => (stand.art === "fertig" ? wasIstNeuAnsicht(stand.liste, laufendeCommits, stand.gesehen, heuteTag, modus) : null),
    // heuteTag ändert sich nur mit dem Tag — der Zeitstempel reicht als Abhängigkeit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stand, laufendeCommits, modus, heute],
  );
  const gesamt = ansicht ? ansicht.gruppen.reduce((n, g) => n + g.eintraege.length, 0) : 0;
  const mehr = Math.max(0, gesamt - MAX_EINTRAEGE);
  // Lange Listen: nur die ersten MAX_EINTRAEGE über alle Tage hinweg, der Rest klappt auf (#1979).
  const sichtbar: AenderungsGruppe[] = React.useMemo(() => {
    if (!ansicht) return [];
    let rest = alleZeigen ? Number.POSITIVE_INFINITY : MAX_EINTRAEGE;
    const out: AenderungsGruppe[] = [];
    for (const g of ansicht.gruppen) {
      if (rest <= 0) break;
      const eintraege = g.eintraege.slice(0, rest);
      rest -= eintraege.length;
      if (eintraege.length) out.push({ ...g, eintraege });
    }
    return out;
  }, [ansicht, alleZeigen]);
  React.useEffect(() => {
    for (const g of sichtbar) for (const e of g.eintraege) angezeigtRef.current.add(e.sha);
  }, [sichtbar]);

  // Beim Schließen (und vor „Jetzt neu laden") gilt alles Angezeigte als gelesen.
  const merke = React.useCallback(() => {
    if (stand.art !== "fertig") return;
    const aktuell = new Set(
      Array.isArray(stand.liste)
        ? (stand.liste as Array<{ sha?: unknown }>).map((a) => a?.sha).filter((x): x is string => typeof x === "string")
        : [],
    );
    merkeGesehen(stand.gesehen, angezeigtRef.current, tageZurueck(heute ?? new Date(), ERSTES_MAL_TAGE), aktuell);
  }, [stand, heute]);
  const schliessen = React.useCallback(() => {
    merke();
    onClose();
  }, [merke, onClose]);

  // Der Fokus springt beim Öffnen auf „Schließen" und beim Schließen zurück.
  React.useEffect(() => {
    if (!offen) return;
    const vorher = aktivesDokument().activeElement as HTMLElement | null;
    schliessenRef.current?.focus();
    return () => vorher?.focus?.();
  }, [offen]);
  // Escape schließt (eigener Effekt: `schliessen` ändert sich mit dem geladenen Stand).
  React.useEffect(() => {
    if (!offen) return;
    const taste = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        schliessen();
      }
    };
    alleDokumente.addEventListener("keydown", taste, true);
    return () => alleDokumente.removeEventListener("keydown", taste, true);
  }, [offen, schliessen]);

  if (!offen || typeof document === "undefined") return null;

  return createPortal(
    <div
      onMouseDown={(ev) => ev.target === ev.currentTarget && schliessen()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(36,21,34,0.38)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: SCHRIFT,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="was-ist-neu-titel"
        style={{
          width: "100%",
          maxWidth: 580,
          maxHeight: "min(80vh, 720px)",
          display: "flex",
          flexDirection: "column",
          background: "#ffffff",
          borderRadius: 14,
          border: "1px solid rgba(125,33,105,0.18)",
          boxShadow: "0 18px 48px rgba(36,21,34,0.22)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "14px 18px",
            background: "linear-gradient(180deg, rgba(248,238,245,0.95) 0%, rgba(248,238,245,0.6) 100%)",
            borderBottom: "1px solid rgba(125,33,105,0.12)",
          }}
        >
          <div id="was-ist-neu-titel" style={{ fontWeight: 700, fontSize: "1.02rem", color: TEXT, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: AUBERGINE, display: "inline-flex" }}>
              <Info16Regular />
            </span>
            Was ist neu?
          </div>
          <button
            type="button"
            onClick={schliessen}
            aria-label="Schließen"
            style={{
              appearance: "none",
              border: "none",
              background: "transparent",
              color: AUBERGINE,
              cursor: "pointer",
              padding: 4,
              display: "inline-flex",
              borderRadius: 8,
            }}
          >
            <Dismiss16Regular />
          </button>
        </div>
        <div style={{ padding: "14px 18px", overflowY: "auto" }}>
          {stand.art === "laedt" && <div style={{ color: LEISE }}>Lädt…</div>}
          {stand.art === "fehler" && <div style={{ color: LEISE }}>Die Liste der Änderungen ist gerade nicht abrufbar.</div>}
          {ansicht && sichtbar.length === 0 && (
            <div style={{ color: LEISE }}>
              {ansicht.nurTechnisches
                ? "Kleinere technische Verbesserungen im Hintergrund."
                : modus === "alle"
                  ? "In den letzten 14 Tagen gab es keine Änderungen, die man bemerkt."
                  : "Sie haben alle Änderungen gelesen."}
            </div>
          )}
          {ansicht && sichtbar.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {sichtbar.map((g) => (
                <section key={g.schluessel} aria-label={g.titel} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: TEXT,
                      paddingBottom: 4,
                      borderBottom: "1px solid rgba(125,33,105,0.12)",
                    }}
                  >
                    {g.titel}
                  </div>
                  {ABSCHNITTE.map(({ art, titel }) => {
                    const liste = g.eintraege.filter((e) => e.art === art);
                    if (!liste.length) return null;
                    return (
                      <div key={art} role="group" aria-label={`${g.titel}: ${titel}`}>
                        <div style={{ fontWeight: 700, fontSize: "0.74rem", letterSpacing: "0.04em", textTransform: "uppercase", color: AUBERGINE, marginBottom: 8 }}>
                          {titel}
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                          {liste.map((e) => (
                            <Stichpunkt key={e.sha} e={e} />
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </section>
              ))}
              {mehr > 0 && (
                <button type="button" aria-expanded={alleZeigen} onClick={() => setAlleZeigen((v) => !v)} style={linkStil}>
                  {alleZeigen ? "Weniger anzeigen" : `… und ${mehr} weitere anzeigen`}
                </button>
              )}
            </div>
          )}
          {ansicht && (
            <button
              type="button"
              onClick={() => {
                setAlleZeigen(false);
                setModus((m) => (m === "alle" ? "ungelesen" : "alle"));
              }}
              style={{ ...linkStil, display: "block", marginTop: 14, fontWeight: 500, color: LEISE }}
            >
              {modus === "alle" ? "Nur ungelesene anzeigen" : "Frühere Änderungen anzeigen"}
            </button>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 18px", borderTop: "1px solid rgba(125,33,105,0.10)" }}>
          <button ref={schliessenRef} type="button" style={knopfStil(false)} onClick={schliessen}>
            Schließen
          </button>
          {onNeuLaden && (
            <button
              type="button"
              style={knopfStil(true)}
              onClick={() => {
                merke();
                onNeuLaden();
              }}
            >
              Jetzt neu laden
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const UpdateHinweis: React.FC<{ aktiv: boolean }> = ({ aktiv }) => {
  const { verfuegbar, spaeter } = useUpdateVerfuegbar();
  const [infoOffen, setInfoOffen] = React.useState(false);
  // „Was ist neu?" im Benutzermenü öffnet dasselbe Popup — auch ohne neue Version.
  React.useEffect(() => {
    if (!aktiv) return;
    const oeffnen = () => setInfoOffen(true);
    window.addEventListener(WAS_IST_NEU_EREIGNIS, oeffnen);
    return () => window.removeEventListener(WAS_IST_NEU_EREIGNIS, oeffnen);
  }, [aktiv]);
  const schliessen = React.useCallback(() => setInfoOffen(false), []);
  if (!aktiv) return null;

  const neuLaden = () => {
    // Bewusstes Neuladen: die globale „Seite verlassen?“-Nachfrage bei
    // ungelesenen Mitteilungen (NavigationLayout) hier nicht auslösen.
    // Ungespeicherte Akten fragen weiterhin selbst (useVerlassenSperre).
    setReloadWarningSuppressed(true);
    window.location.reload();
  };

  return (
    <>
    <WasIstNeuDialog offen={infoOffen} onClose={schliessen} onNeuLaden={verfuegbar ? neuLaden : undefined} />
    {verfuegbar && (
    <div
      role="region"
      aria-label="Neue Version verfügbar"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        flexWrap: "wrap",
        padding: "10px 14px",
        margin: "6px 4px 0",
        borderRadius: 12,
        border: "1px solid rgba(125,33,105,0.18)",
        background: "linear-gradient(180deg, rgba(248,238,245,0.95) 0%, rgba(248,238,245,0.75) 100%)",
        fontFamily: SCHRIFT,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
        <span style={{ color: AUBERGINE, flexShrink: 0, marginTop: 1 }}>
          <ArrowSync20Regular />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: "0.92rem", color: "#241522" }}>
            Neue Version von KMVS verfügbar
            <button
              type="button"
              onClick={() => setInfoOffen(true)}
              aria-label="Was ist neu? (Infosymbol)"
              title="Was ist neu?"
              style={{
                appearance: "none",
                border: "none",
                background: "transparent",
                color: AUBERGINE,
                cursor: "pointer",
                padding: 2,
                display: "inline-flex",
                borderRadius: 999,
              }}
            >
              <Info16Regular />
            </button>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#5b4a58", marginTop: 2 }}>
            Bitte neu laden, damit Sie die neuesten Funktionen und Korrekturen haben. Ungespeicherte Eingaben vorher speichern.
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
        <button type="button" style={knopfStil(false)} onClick={() => setInfoOffen(true)}>
          <Info16Regular style={{ verticalAlign: "-3px", marginRight: 4 }} />
          Was ist neu?
        </button>
        <button type="button" style={knopfStil(true)} onClick={neuLaden}>
          Jetzt neu laden
        </button>
        <button type="button" style={knopfStil(false)} onClick={spaeter} aria-label="Hinweis für 30 Minuten ausblenden">
          <Dismiss16Regular style={{ verticalAlign: "-2px", marginRight: 4 }} />
          Später
        </button>
      </div>
    </div>
    )}
    </>
  );
};

export default UpdateHinweis;
